/**
 * THE CLIENT'S PAGE (R10.1, ADR-0015 §R10).
 *
 * Opened from a link an accountant sent. The person here has no Leveri
 * account and never signs in: the link is the credential, scoped to ONE
 * request. They read what is asked and send a file; nothing else is reachable.
 *
 * THE TOKEN lives in the URL FRAGMENT (`/r#<token>`), which a browser never
 * sends to any server, and it leaves this page only inside a request BODY —
 * never a path, query or header, so no request line or access log holds it.
 * Every way a link can fail reads the same, because the server answers them
 * the same.
 *
 * Mounted BEFORE the app's sign-in gate (main.tsx): this page makes no
 * session call and carries no credentials (`credentials: "omit"`).
 */
import { useCallback, useEffect, useState } from "react";
import type { z } from "zod";
import { ErrorEnvelope, PublicRequestResponse, PublicRespondResponse } from "@leveri/contracts";
import { Citation, FileButton, FileTitle, Label, Notice, Reading, Section, SectionHead, Statement } from "../file/index.js";

type View = z.infer<typeof PublicRequestResponse>["request"];

const tokenFromFragment = (): string => window.location.hash.replace(/^#/, "");

async function post<T extends z.ZodTypeAny>(path: string, schema: T, body: BodyInit, json: boolean): Promise<z.infer<T>> {
  const res = await fetch(path, {
    method: "POST", credentials: "omit", body,
    headers: json ? { accept: "application/json", "content-type": "application/json" } : { accept: "application/json" },
  });
  const payload: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const envelope = ErrorEnvelope.safeParse(payload);
    throw new Error(envelope.success ? envelope.data.error.message : "Something went wrong. Try again in a moment.");
  }
  return schema.parse(payload);
}

export function ClientRequestPage() {
  const [token] = useState(tokenFromFragment);
  const [view, setView] = useState<View | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await post("/api/public/request", PublicRequestResponse, JSON.stringify({ token }), true);
      setView(result.request);
      setFailure(null);
    } catch (err) {
      setFailure(err instanceof Error ? err.message : String(err));
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  async function send(files: File[]) {
    const file = files[0];
    if (!file || sending) return;
    setSending(true);
    setSent(null);
    try {
      const form = new FormData();
      form.append("token", token);
      form.append("file", file, file.name);
      const result = await post("/api/public/request/respond", PublicRespondResponse, form, false);
      setSent(result.received.filename);
      await load();
    } catch (err) {
      setFailure(err instanceof Error ? err.message : String(err));
    } finally {
      setSending(false);
    }
  }

  return (
    <main className="mx-auto max-w-[640px] px-6 py-14">
      {!view && !failure && <Reading>Opening the request…</Reading>}
      {!view && failure && (
        <>
          <FileTitle kicker="Leveri">This link cannot be used</FileTitle>
          <p className="mt-4 text-sm text-ink-soft" data-testid="client-request-invalid">{failure}</p>
        </>
      )}
      {view && (
        <>
          <FileTitle kicker={`${view.firmName} asks`} meta={`For ${view.contactName} · open until ${view.expiresAtText}`}>
            {view.requirement}
          </FileTitle>
          <Section className="mt-8">
            <Statement>{view.detail}</Statement>
          </Section>
          <Section className="mt-8">
            <SectionHead>Send it</SectionHead>
            <p className="text-sm text-ink-soft">A spreadsheet or CSV export, as your system produces it. One file at a time; send another if there is more.</p>
            <div className="mt-4">
              <FileButton variant="primary" accept=".csv,.xlsx,.xls" disabled={sending} onFiles={(files) => void send(files)}>
                {sending ? "Sending…" : "Choose a file to send"}
              </FileButton>
            </div>
            {sent && <p className="mt-3 text-sm text-ink" data-testid="client-request-sent">Received: {sent}. {view.firmName} will review it.</p>}
            {failure && <Notice className="mt-3">{failure}</Notice>}
          </Section>
          {view.responses.length > 0 && (
            <Section className="mt-8">
              <Label>Already received</Label>
              <ul className="mt-2 flex flex-col gap-1">
                {view.responses.map((r, i) => (
                  <li key={`${r.filename}-${r.receivedAt}-${i}`} className="text-sm text-ink">
                    {r.filename} <Citation>{r.receivedAtText}</Citation>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </>
      )}
    </main>
  );
}
